import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Apple, ArrowRight, Loader2, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { db, auth } from "@/firebase/client";
import { toastError } from "@/components/ui/toast";
import { useNavigate } from "react-router-dom";
import googleIcon from "../assets/google-icon.svg";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

interface AuthDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  hasGuestSticker?: boolean;
  showFreeTokensMessage?: boolean;
  redirectTo?: string; // Path to redirect to after successful auth
}

const GUEST_STICKER_KEY = "guest-sticker-data";
const AUTH_REDIRECT_KEY = "auth-redirect-to";

const AuthDialog = ({
  isOpen,
  onOpenChange,
  hasGuestSticker = false,
  redirectTo,
}: AuthDialogProps) => {
  // Store redirect URL in sessionStorage for OAuth flows
  if (redirectTo && isOpen) {
    sessionStorage.setItem(AUTH_REDIRECT_KEY, redirectTo);
  }
  const [activeTab, setActiveTab] = useState<"login" | "register">("login");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Form data
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const { signIn, signUp, signInWithOAuth } = useAuth();

  const saveGuestStickerToAccount = async (userId: string) => {
    try {
      // Get sticker data from localStorage
      const guestStickerJson = localStorage.getItem(GUEST_STICKER_KEY);
      if (!guestStickerJson) return;

      type GuestStickerData = {
        stickerData: {
          description: string;
          shape: string;
          size: string;
          artStyle: string;
        };
        imageUrl: string;
      };

      const guestSticker: GuestStickerData = JSON.parse(guestStickerJson);

      const projectData = await addDoc(collection(db, "projects"), {
        description: guestSticker.stickerData.description.substring(0, 100),
        user_id: userId,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        is_archived: false,
      });

      await addDoc(collection(db, "designs"), {
        project_id: projectData.id,
        image_url: guestSticker.imageUrl,
        prompt: guestSticker.stickerData.description,
        shape: guestSticker.stickerData.shape,
        size: guestSticker.stickerData.size,
        art_style: guestSticker.stickerData.artStyle,
        is_current: true,
        created_at: new Date().toISOString(),
      });

      localStorage.removeItem(GUEST_STICKER_KEY);

      return projectData.id;
    } catch (error) {
      console.error("Error saving guest sticker:", error);
      toastError({
        title: "Error",
        description: "There was an error saving your sticker",
      });
      return null;
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    const { error } = await signIn(email, password);

    if (error) {
      setIsSubmitting(false);
      setError(error.message);
    } else {
      if (hasGuestSticker) {
        const user = auth.currentUser;

        if (user) {
          await saveGuestStickerToAccount(user.uid);

          onOpenChange(false);
          resetForm();

          navigate("/dashboard");
        }
      } else {
        onOpenChange(false);
        resetForm();
        if (redirectTo) {
          navigate(redirectTo);
        }
      }
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    const { error, existingUser } = await signUp(email, password, firstName, lastName);

    if (error) {
      setIsSubmitting(false);
      setError(error.message);
      if (existingUser) {
        setActiveTab("login");
      }
    } else {
      if (hasGuestSticker) {
        const user = auth.currentUser;

        if (user) {
          await saveGuestStickerToAccount(user.uid);

          onOpenChange(false);
          resetForm();

          navigate("/dashboard");
        }
      } else {
        onOpenChange(false);
        resetForm();
        if (redirectTo) {
          navigate(redirectTo);
        }
      }
    }
  };

  const handleOAuthSignIn = async (provider: "google" | "apple") => {
    await signInWithOAuth(provider);
  };

  const resetForm = () => {
    setEmail("");
    setPassword("");
    setFirstName("");
    setLastName("");
    setError(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl font-bold">
            Let&apos;s make something cool!
          </DialogTitle>
          <DialogDescription className="text-center">
            Sign in or get started for free.
          </DialogDescription>
        </DialogHeader>

        {hasGuestSticker && (
          <Alert className="bg-secondary/10 text-secondary border-secondary/30">
            <p className="text-sm">
              You&apos;ve created a sticker! Sign in or create an account to save it to your
              collection.
            </p>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Tabs
          defaultValue="login"
          value={activeTab}
          onValueChange={value => {
            setActiveTab(value as "login" | "register");
            setError(null);
          }}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login" data-testid="auth-tab-login">
              Login
            </TabsTrigger>
            <TabsTrigger value="register" data-testid="auth-tab-register">
              Register
            </TabsTrigger>
          </TabsList>

          <TabsContent value="login" className="mt-4">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email-login">Email</Label>
                <Input
                  id="email-login"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  data-testid="auth-email-input"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password-login">Password</Label>
                  <a href="#" className="text-xs text-secondary hover:underline">
                    Forgot password?
                  </a>
                </div>
                <Input
                  id="password-login"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  data-testid="auth-password-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-secondary hover:bg-secondary/90"
                disabled={isSubmitting}
                data-testid="auth-submit-button"
                aria-busy={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                  </>
                )}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="register" className="mt-4">
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first-name">First name</Label>
                  <Input
                    id="first-name"
                    placeholder="John"
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    required
                    data-testid="auth-firstname-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last-name">Last name</Label>
                  <Input
                    id="last-name"
                    placeholder="Doe"
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    required
                    data-testid="auth-lastname-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email-register">Email</Label>
                <Input
                  id="email-register"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password-register">Password</Label>
                <Input
                  id="password-register"
                  type="password"
                  placeholder="Create a strong password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-secondary hover:bg-secondary/90"
                disabled={isSubmitting}
                data-testid="auth-submit-button"
                aria-busy={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                    Creating account...
                  </>
                ) : (
                  <>
                    Create Account
                    <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                  </>
                )}
              </Button>
            </form>
          </TabsContent>
        </Tabs>

        <div className="relative my-2">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200"></div>
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="bg-white px-2 text-gray-500">Or continue with</span>
          </div>
        </div>

        <div className="flex flex-col space-y-2">
          <Button
            variant="outline"
            type="button"
            className="w-full"
            onClick={() => handleOAuthSignIn("google")}
            data-testid="auth-google-button"
            aria-label="Sign in with Google"
          >
            <img src={googleIcon} alt="" className="mr-2 h-4 w-4" aria-hidden="true" />
            Continue with Google
          </Button>
          <Button
            variant="outline"
            type="button"
            className="w-full"
            onClick={() => handleOAuthSignIn("apple")}
            data-testid="auth-apple-button"
            aria-label="Sign in with Apple"
          >
            <Apple className="mr-2 h-4 w-4" aria-hidden="true" />
            Continue with Apple
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuthDialog;
